/**
 * What a line and a case mean, in one place for the workbench and the Outlook pane.
 *
 * End user review, 11 Oct 2026 (F05): the Outlook pane read the raw proposed item and
 * the legacy line state, so after a person picked item B or "No item" it still showed
 * proposal A as a positive match, and a suggestion looked matched. Both surfaces now use
 * these helpers:
 *
 * - `lineStatusFor`: how far the engine got (matched, suggested, open, failed);
 * - `effectiveItem`: the item a line stands for once a person decided;
 * - `lineOutcome`: the item a person would quote now and whether it still needs a
 *   person (a suggestion asks for confirmation, "No item" shows no item);
 * - `replyRevisionOf`: a key of everything a reply rests on, so the reply panel reads
 *   its freshness again when the case changes (F06);
 * - `replyEligible`: whether the reply panel belongs on the case, with or without
 *   extracted lines (F07).
 *
 * Pure: no React, no requests.
 */

import { isOutOfScopeCase } from "@/lib/intents";
import { type LineStatus, type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";

/** The line's status; a fixture line without one is derived from its older state. */
export function lineStatusFor(line: QuoteLine): LineStatus {
  if (line.status) {
    return line.status;
  }
  if (line.state === "review") {
    return "open";
  }
  return line.state === "assisted" ? "suggested" : "matched";
}

/** The item a line stands for once a rep decided: the picked item, none, or the engine's. */
export function effectiveItem(line: QuoteLine): { item: string | null; name: string | null } {
  if (line.requestAction === "remove") {
    return { item: null, name: null };
  }
  const decision = line.decision;
  if (decision?.kind === "no_item") {
    return { item: null, name: null };
  }
  if (decision?.kind === "item" || (decision?.kind === "confirm" && decision.item)) {
    return { item: decision.item ?? null, name: decision.item_name ?? null };
  }
  return { item: line.matchedItemId, name: line.itemName ?? null };
}

/** Where one line stands for sales, the same on every surface. */
export type LineOutcome =
  | { kind: "removed" }
  /** Nothing to review: the whole mail or a signature stood in for a line. */
  | { kind: "not_a_line" }
  /** The engine could not evaluate it: check by hand. */
  | { kind: "failed" }
  /** A person said there is no item for this line. */
  | { kind: "no_item" }
  /** A person confirmed or picked this item (or I2Q decided it by its exact number). */
  | { kind: "decided"; item: string; byI2q: boolean }
  /** The engine matched this item and nobody decided yet. */
  | { kind: "matched"; item: string }
  /** The engine suggests this item: a person confirms it. */
  | { kind: "suggested"; item: string }
  /** No item yet: a person picks one. */
  | { kind: "open" };

/** The line as sales should read it now: a person's decision first, then the engine's. */
export function lineOutcome(line: QuoteLine): LineOutcome {
  if (line.requestAction === "remove") {
    return { kind: "removed" };
  }
  if (line.fallback) {
    return { kind: "not_a_line" };
  }
  const decision = line.decision;
  if (decision?.kind === "no_item") {
    return { kind: "no_item" };
  }
  const decided = decision ? effectiveItem(line).item : null;
  if (decision && decided) {
    return { kind: "decided", item: decided, byI2q: Boolean(decision.by_i2q) };
  }
  const status = lineStatusFor(line);
  if (status === "failed") {
    return { kind: "failed" };
  }
  if (status === "matched" && line.matchedItemId) {
    return { kind: "matched", item: line.matchedItemId };
  }
  const suggested = line.matchedItemId ?? line.suggestions?.[0]?.item ?? null;
  if (status === "suggested" && suggested) {
    return { kind: "suggested", item: suggested };
  }
  return { kind: "open" };
}

/** A line's cell in the Outlook status strip: positive only for an item that holds. */
export function paneLineStatus(line: QuoteLine): {
  status: "positive" | "caution" | "negative" | "neutral";
  result: string;
} {
  const outcome = lineOutcome(line);
  switch (outcome.kind) {
    case "removed":
      return { status: "neutral", result: "Customer removed this request" };
    case "not_a_line":
      return { status: "caution", result: "no lines found" };
    case "failed":
      return { status: "caution", result: "not evaluated" };
    case "no_item":
      return { status: "neutral", result: "no item" };
    case "decided":
    case "matched":
      return { status: "positive", result: outcome.item };
    case "suggested":
      return { status: "caution", result: `confirm ${outcome.item}` };
    case "open":
      return { status: "caution", result: "your pick" };
  }
}

/**
 * Everything a reply rests on, as one key: the customer, every line's item, quantity,
 * unit, status, decision and price, and the work version. It changes after a decision,
 * its undo, a cleared customer and a re-evaluation; the reply panel then reads the
 * reply's freshness from the server again.
 */
export function replyRevisionOf(detail: QuoteCaseDetail): string {
  const lines = detail.lines.map((line) =>
    [
      line.lineKey ?? line.id,
      line.matchedItemId ?? "",
      line.qty ?? "",
      line.unit ?? "",
      lineStatusFor(line),
      line.requestAction ?? "",
      line.decision?.kind ?? "",
      line.decision?.item ?? "",
      line.price?.status ?? "",
      line.price?.unitPrice ?? "",
      line.price?.currency ?? "",
    ].join("~"),
  );
  return [
    detail.work?.version ?? 0,
    detail.work?.customer_account ?? "",
    detail.customerAccount ?? "",
    detail.customerCertainty ?? "",
    detail.state,
    ...lines,
  ].join("|");
}

/**
 * True when a reply can be prepared for the case, also without extracted lines (review
 * F07, 11 Oct 2026): an inquiry with work data that I2Q has read, that is not a duplicate
 * and not "not an inquiry". A case without lines most often needs a question to the
 * customer, so the reply panel is shown there too; the server still refuses a case it
 * cannot draft, and the panel then says why.
 */
export function replyEligible(detail: QuoteCaseDetail): boolean {
  const hasWork = detail.work != null || detail.workStatus != null;
  if (!hasWork || isOutOfScopeCase(detail)) {
    return false;
  }
  return !["queued", "processing", "duplicate", "not-an-rfq"].includes(detail.state);
}
